<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import ReportSchedulesAPI from 'dashboard/api/reportSchedules';
import ReportHeader from './components/ReportHeader.vue';
import SummaryEntityFilters from './components/SummaryEntityFilters.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { generateSummaryEntityFilterParams } from './helpers/reportFilterHelper';
import {
  RESOLUTION_METHOD_OPTIONS,
  HAS_COMMENT_OPTIONS,
  buildRatingsList,
} from './components/Csat/CsatFilterHelpers';

const { t } = useI18n();
const store = useStore();

const loading = ref(false);
const saving = ref(false);
const schedules = ref([]);
const showForm = ref(false);
const editingId = ref(null);
const recipientsText = ref('');
const entityFilters = ref({});
const entityFiltersKey = ref(0);

const emptyExtras = () => ({
  botOutcome: 'all',
  entryType: 'all',
  result: 'all',
  ratings: [],
  resolutionMethod: 'all',
  hasComment: 'all',
});

const emptyForm = () => ({
  section: 'overview',
  frequency: 'daily',
  send_hour: '8',
  send_minute: '0',
  enabled: true,
  ...emptyExtras(),
});

const form = ref(emptyForm());

const sectionToFilterType = section => {
  const map = {
    overview: 'account',
    chats: 'chats',
    agents: 'agent',
    activity: 'agent_activity',
    bot: 'bot',
    teams: 'team',
    tags: 'label',
    sources: 'inbox',
    queue: 'queue',
  };
  return map[section] || null;
};

const filterType = computed(() => sectionToFilterType(form.value.section));
const showCsatFilters = computed(() => form.value.section === 'csat');
const showBotOutcome = computed(() => form.value.section === 'bot');
const showQueueExtras = computed(() => form.value.section === 'queue');

const sectionOptions = computed(() =>
  [
    'overview',
    'chats',
    'agents',
    'activity',
    'bot',
    'teams',
    'tags',
    'sources',
    'queue',
    'csat',
  ].map(value => ({
    value,
    label: t(`REPORT_SCHEDULES.SECTIONS.${value.toUpperCase()}`),
  }))
);

const frequencyOptions = computed(() =>
  ['daily', 'weekly', 'monthly'].map(value => ({
    value,
    label: t(`REPORT_SCHEDULES.FREQUENCY.${value.toUpperCase()}`),
  }))
);

const hourOptions = computed(() =>
  Array.from({ length: 24 }, (_, hour) => ({
    value: String(hour),
    label: `${String(hour).padStart(2, '0')}:00`,
  }))
);

const minuteOptions = computed(() =>
  Array.from({ length: 60 }, (_, minute) => ({
    value: String(minute),
    label: String(minute).padStart(2, '0'),
  }))
);

const botOutcomeOptions = computed(() => [
  { value: 'all', label: t('BOT_REPORTS.FILTERS.OUTCOME_ALL') },
  {
    value: 'resolved_by_bot',
    label: t('BOT_REPORTS.FILTERS.OUTCOME_RESOLVED'),
  },
  { value: 'handoff', label: t('BOT_REPORTS.FILTERS.OUTCOME_HANDOFF') },
  { value: 'fallback', label: t('BOT_REPORTS.FILTERS.OUTCOME_FALLBACK') },
]);

const queueResultOptions = computed(() => [
  { value: 'all', label: t('QUEUED_CUSTOMERS_REPORTS.FILTERS.RESULT_ALL') },
  {
    value: 'accepted',
    label: t('QUEUED_CUSTOMERS_REPORTS.FILTERS.RESULT_ACCEPTED'),
  },
  {
    value: 'abandoned',
    label: t('QUEUED_CUSTOMERS_REPORTS.FILTERS.RESULT_ABANDONED'),
  },
  {
    value: 'waiting',
    label: t('QUEUED_CUSTOMERS_REPORTS.FILTERS.RESULT_WAITING'),
  },
]);

const queueEntryTypeOptions = computed(() => [
  {
    value: 'all',
    label: t('QUEUED_CUSTOMERS_REPORTS.FILTERS.ENTRY_ALL'),
  },
  {
    value: 'bot_handoff',
    label: t('QUEUED_CUSTOMERS_REPORTS.FILTERS.ENTRY_BOT_HANDOFF'),
  },
  {
    value: 'requeue',
    label: t('QUEUED_CUSTOMERS_REPORTS.FILTERS.ENTRY_REQUEUE'),
  },
]);

const ratingOptions = computed(() =>
  buildRatingsList(t, 'emoji').map(item => ({
    value: item.id,
    label: item.name,
  }))
);
const resolutionMethodOptions = computed(() => [
  { value: 'all', label: t('SUMMARY_REPORTS.FILTERS.RESOLUTION_METHOD.ALL') },
  ...RESOLUTION_METHOD_OPTIONS(t).map(item => ({
    value: item.id,
    label: item.name,
  })),
]);
const hasCommentOptions = computed(() => [
  { value: 'all', label: t('CSAT_REPORTS.FILTERS.HAS_COMMENT.LABEL') },
  ...HAS_COMMENT_OPTIONS(t).map(item => ({
    value: item.id,
    label: item.name,
  })),
]);

const parseRecipients = () =>
  recipientsText.value
    .split(/[,\s;]+/)
    .map(email => email.trim().toLowerCase())
    .filter(Boolean);

const emailValid = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const fetchSchedules = async () => {
  loading.value = true;
  try {
    const { data } = await ReportSchedulesAPI.get();
    schedules.value = data.payload || [];
  } catch {
    useAlert(t('REPORT_SCHEDULES.API.FETCH_ERROR'));
  } finally {
    loading.value = false;
  }
};

const filtersFromSchedule = schedule => {
  const data = schedule.filters || {};
  return {
    agentIds: data.agent_ids || [],
    inboxIds: data.inbox_ids || [],
    teamIds: data.team_ids || [],
    labelIds: data.label_ids || [],
    excludeLabelIds: data.exclude_label_ids || [],
    channelTypes: data.channel_types || [],
    episodeResult: data.episode_result || 'all',
    receiptType: data.receipt_type || 'all',
    tagPresence: data.tag_presence || 'all',
    resolutionMethod: data.resolution_method || 'all',
    statusAtPeriodEnd: data.status_at_period_end || 'all',
    reopening: data.reopening || 'all',
    transfers: data.transfers || 'all',
    humanResponse: data.human_response || 'all',
  };
};

const openCreate = () => {
  editingId.value = null;
  form.value = emptyForm();
  entityFilters.value = {};
  entityFiltersKey.value += 1;
  recipientsText.value = '';
  showForm.value = true;
};

const openEdit = schedule => {
  editingId.value = schedule.id;
  const data = schedule.filters || {};
  form.value = {
    section: schedule.section,
    frequency: schedule.frequency,
    send_hour: String(schedule.send_hour ?? 8),
    send_minute: String(schedule.send_minute ?? 0),
    enabled: schedule.enabled,
    botOutcome: data.bot_outcome || 'all',
    entryType: data.entry_type || 'all',
    result: data.result || 'all',
    ratings: data.rating || [],
    resolutionMethod: data.resolution_method || 'all',
    hasComment: data.has_comment || 'all',
  };
  entityFilters.value = filtersFromSchedule(schedule);
  entityFiltersKey.value += 1;
  recipientsText.value = (schedule.recipients || []).join(', ');
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  editingId.value = null;
};

const onEntityFilterChange = filters => {
  entityFilters.value = filters;
};

const buildFiltersPayload = () => {
  const filters = {
    ...generateSummaryEntityFilterParams(entityFilters.value || {}),
  };

  // Convert comma-joined URL helper output into arrays for jsonb storage.
  [
    'agent_ids',
    'inbox_ids',
    'team_ids',
    'label_ids',
    'exclude_label_ids',
    'channel_types',
  ].forEach(key => {
    if (typeof filters[key] === 'string') {
      filters[key] = filters[key]
        .split(',')
        .map(value => (key === 'channel_types' ? value : Number(value)))
        .filter(Boolean);
    }
  });

  if (showBotOutcome.value && form.value.botOutcome !== 'all') {
    filters.bot_outcome = form.value.botOutcome;
  }
  if (showQueueExtras.value) {
    if (form.value.entryType !== 'all')
      filters.entry_type = form.value.entryType;
    if (form.value.result !== 'all') filters.result = form.value.result;
  }
  if (showCsatFilters.value) {
    if (form.value.ratings?.length) filters.rating = form.value.ratings;
    if (form.value.resolutionMethod !== 'all') {
      filters.resolution_method = form.value.resolutionMethod;
    }
    if (form.value.hasComment !== 'all') {
      filters.has_comment = form.value.hasComment;
    }
    if (entityFilters.value?.agentIds?.length) {
      filters.agent_ids = entityFilters.value.agentIds;
    }
    if (entityFilters.value?.inboxIds?.length) {
      filters.inbox_ids = entityFilters.value.inboxIds;
    }
    if (entityFilters.value?.teamIds?.length) {
      filters.team_ids = entityFilters.value.teamIds;
    }
    if (entityFilters.value?.labelIds?.length) {
      filters.label_ids = entityFilters.value.labelIds;
    }
    if (entityFilters.value?.excludeLabelIds?.length) {
      filters.exclude_label_ids = entityFilters.value.excludeLabelIds;
    }
  }

  return filters;
};

const buildPayload = () => {
  const recipients = parseRecipients();
  if (!recipients.length || recipients.some(email => !emailValid(email))) {
    throw new Error('invalid_recipients');
  }

  return {
    section: form.value.section,
    frequency: form.value.frequency,
    send_hour: Number(form.value.send_hour),
    send_minute: Number(form.value.send_minute),
    enabled: form.value.enabled,
    recipients,
    filters: buildFiltersPayload(),
  };
};

const saveSchedule = async () => {
  saving.value = true;
  try {
    const payload = { report_schedule: buildPayload() };
    if (editingId.value) {
      await ReportSchedulesAPI.update(editingId.value, payload);
      useAlert(t('REPORT_SCHEDULES.API.UPDATE_SUCCESS'));
    } else {
      await ReportSchedulesAPI.create(payload);
      useAlert(t('REPORT_SCHEDULES.API.CREATE_SUCCESS'));
    }
    closeForm();
    await fetchSchedules();
  } catch (error) {
    if (error.message === 'invalid_recipients') {
      useAlert(t('REPORT_SCHEDULES.API.INVALID_RECIPIENTS'));
    } else {
      useAlert(t('REPORT_SCHEDULES.API.SAVE_ERROR'));
    }
  } finally {
    saving.value = false;
  }
};

const toggleEnabled = async schedule => {
  try {
    await ReportSchedulesAPI.update(schedule.id, {
      report_schedule: { enabled: !schedule.enabled },
    });
    await fetchSchedules();
  } catch {
    useAlert(t('REPORT_SCHEDULES.API.SAVE_ERROR'));
  }
};

const removeSchedule = async schedule => {
  try {
    await ReportSchedulesAPI.delete(schedule.id);
    useAlert(t('REPORT_SCHEDULES.API.DELETE_SUCCESS'));
    await fetchSchedules();
  } catch {
    useAlert(t('REPORT_SCHEDULES.API.DELETE_ERROR'));
  }
};

const sendTest = async schedule => {
  try {
    await ReportSchedulesAPI.sendTest(schedule.id);
    useAlert(t('REPORT_SCHEDULES.API.TEST_SUCCESS'));
  } catch {
    useAlert(t('REPORT_SCHEDULES.API.TEST_ERROR'));
  }
};

const formatTime = value => {
  if (!value) return '—';
  return new Date(value).toISOString().replace('.000Z', 'Z');
};

const sectionLabel = section =>
  t(`REPORT_SCHEDULES.SECTIONS.${String(section).toUpperCase()}`);

const frequencyLabel = frequency =>
  t(`REPORT_SCHEDULES.FREQUENCY.${String(frequency).toUpperCase()}`);

const scheduleLabel = schedule =>
  `${frequencyLabel(schedule.frequency)} · ${String(schedule.send_hour).padStart(2, '0')}:${String(schedule.send_minute).padStart(2, '0')} UTC`;

watch(
  () => form.value.section,
  () => {
    Object.assign(form.value, emptyExtras());
    entityFilters.value = {};
    entityFiltersKey.value += 1;
  }
);

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
  fetchSchedules();
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <ReportHeader :header-title="$t('REPORT_SCHEDULES.HEADER')">
      <V4Button
        :label="$t('REPORT_SCHEDULES.CREATE')"
        icon="i-lucide-plus"
        size="sm"
        @click="openCreate"
      />
    </ReportHeader>

    <p class="text-sm text-n-slate-11">
      {{ $t('REPORT_SCHEDULES.DESCRIPTION') }}
    </p>

    <section
      v-if="showForm"
      class="rounded-xl bg-n-solid-2 px-5 py-4 outline outline-1 outline-n-container"
    >
      <h3 class="mb-4 text-base font-medium text-n-slate-12">
        {{
          editingId
            ? $t('REPORT_SCHEDULES.EDIT_TITLE')
            : $t('REPORT_SCHEDULES.CREATE_TITLE')
        }}
      </h3>

      <div class="flex flex-col gap-4">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label class="flex min-w-0 flex-col gap-1.5">
            <span class="text-xs text-n-slate-11">
              {{ $t('REPORT_SCHEDULES.FORM.SECTION') }}
            </span>
            <ComboBox
              v-model="form.section"
              :options="sectionOptions"
              :placeholder="$t('REPORT_SCHEDULES.FORM.SECTION')"
            />
          </label>
          <label class="flex min-w-0 flex-col gap-1.5">
            <span class="text-xs text-n-slate-11">
              {{ $t('REPORT_SCHEDULES.FORM.FREQUENCY') }}
            </span>
            <ComboBox
              v-model="form.frequency"
              :options="frequencyOptions"
              :placeholder="$t('REPORT_SCHEDULES.FORM.FREQUENCY')"
            />
          </label>
          <label class="flex min-w-0 flex-col gap-1.5">
            <span class="text-xs text-n-slate-11">
              {{ $t('REPORT_SCHEDULES.FORM.HOUR') }}
            </span>
            <select
              v-model="form.send_hour"
              class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12"
            >
              <option
                v-for="option in hourOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="flex min-w-0 flex-col gap-1.5">
            <span class="text-xs text-n-slate-11">
              {{ $t('REPORT_SCHEDULES.FORM.MINUTE') }}
            </span>
            <select
              v-model="form.send_minute"
              class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12"
            >
              <option
                v-for="option in minuteOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
        </div>

        <div>
          <span class="mb-1.5 block text-xs text-n-slate-11">
            {{ $t('REPORT_SCHEDULES.FORM.FILTERS') }}
          </span>
          <SummaryEntityFilters
            v-if="filterType"
            :key="`${form.section}-${entityFiltersKey}`"
            :type="filterType"
            :sync-url="false"
            :initial-filters="entityFilters"
            :hide-resolution-method="
              ['bot', 'queue', 'agent_activity'].includes(filterType)
            "
            @filter-change="onEntityFilterChange"
          />
          <SummaryEntityFilters
            v-else-if="showCsatFilters"
            :key="`csat-${entityFiltersKey}`"
            type="account"
            :sync-url="false"
            :initial-filters="entityFilters"
            hide-resolution-method
            @filter-change="onEntityFilterChange"
          />

          <div
            v-if="showBotOutcome || showQueueExtras || showCsatFilters"
            class="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3"
          >
            <ComboBox
              v-if="showBotOutcome"
              v-model="form.botOutcome"
              :options="botOutcomeOptions"
              :placeholder="$t('BOT_REPORTS.FILTERS.BOT_OUTCOME')"
            />
            <ComboBox
              v-if="showQueueExtras"
              v-model="form.entryType"
              :options="queueEntryTypeOptions"
              :placeholder="$t('QUEUED_CUSTOMERS_REPORTS.FILTERS.ENTRY_TYPE')"
            />
            <ComboBox
              v-if="showQueueExtras"
              v-model="form.result"
              :options="queueResultOptions"
              :placeholder="$t('QUEUED_CUSTOMERS_REPORTS.FILTERS.RESULT')"
            />
            <template v-if="showCsatFilters">
              <TagMultiSelectComboBox
                v-model="form.ratings"
                :options="ratingOptions"
                :placeholder="$t('CSAT_REPORTS.FILTERS.RATINGS.LABEL')"
              />
              <ComboBox
                v-model="form.resolutionMethod"
                :options="resolutionMethodOptions"
                :placeholder="$t('CSAT_REPORTS.FILTERS.RESOLVED_BY.LABEL')"
              />
              <ComboBox
                v-model="form.hasComment"
                :options="hasCommentOptions"
                :placeholder="$t('CSAT_REPORTS.FILTERS.HAS_COMMENT.LABEL')"
              />
            </template>
          </div>
        </div>

        <label class="flex flex-col gap-1.5">
          <span class="text-xs text-n-slate-11">
            {{ $t('REPORT_SCHEDULES.FORM.RECIPIENTS') }}
          </span>
          <input
            v-model="recipientsText"
            type="text"
            class="h-10 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12"
            :placeholder="$t('REPORT_SCHEDULES.FORM.RECIPIENTS_PLACEHOLDER')"
          />
        </label>

        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="form.enabled" type="checkbox" class="rounded" />
          {{ $t('REPORT_SCHEDULES.FORM.ENABLED') }}
        </label>
      </div>

      <div class="mt-4 flex gap-2">
        <V4Button
          :label="$t('REPORT_SCHEDULES.FORM.SAVE')"
          size="sm"
          :is-loading="saving"
          @click="saveSchedule"
        />
        <V4Button
          :label="$t('REPORT_SCHEDULES.FORM.CANCEL')"
          size="sm"
          color="slate"
          variant="faded"
          @click="closeForm"
        />
      </div>
    </section>

    <section
      class="overflow-hidden rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
    >
      <div class="overflow-x-auto">
        <table class="w-full min-w-[960px] text-xs">
          <thead>
            <tr class="border-b border-n-weak text-n-slate-11">
              <th class="px-3 py-2 text-left font-medium">
                {{ $t('REPORT_SCHEDULES.TABLE.SECTION') }}
              </th>
              <th class="px-3 py-2 text-left font-medium">
                {{ $t('REPORT_SCHEDULES.TABLE.FILTERS') }}
              </th>
              <th class="px-3 py-2 text-left font-medium">
                {{ $t('REPORT_SCHEDULES.TABLE.SCHEDULE') }}
              </th>
              <th class="px-3 py-2 text-left font-medium">
                {{ $t('REPORT_SCHEDULES.TABLE.RECIPIENTS') }}
              </th>
              <th class="px-3 py-2 text-left font-medium">
                {{ $t('REPORT_SCHEDULES.TABLE.LAST_SENT') }}
              </th>
              <th class="px-3 py-2 text-left font-medium">
                {{ $t('REPORT_SCHEDULES.TABLE.NEXT_SENT') }}
              </th>
              <th class="px-3 py-2 text-left font-medium">
                {{ $t('REPORT_SCHEDULES.TABLE.STATUS') }}
              </th>
              <th class="px-3 py-2 text-right font-medium">
                {{ $t('REPORT_SCHEDULES.TABLE.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="schedule in schedules"
              :key="schedule.id"
              class="border-b border-n-weak/60"
            >
              <td class="px-3 py-2 text-n-slate-12">
                {{ sectionLabel(schedule.section) }}
              </td>
              <td class="px-3 py-2 text-n-slate-11">
                {{ schedule.filters_summary || '—' }}
              </td>
              <td class="px-3 py-2 text-n-slate-12">
                {{ scheduleLabel(schedule) }}
              </td>
              <td class="px-3 py-2 text-n-slate-12">
                {{ (schedule.recipients || []).join(', ') }}
              </td>
              <td class="px-3 py-2 text-n-slate-11">
                <div>{{ formatTime(schedule.last_sent_at) }}</div>
                <div v-if="schedule.last_status">
                  {{ schedule.last_status }}
                </div>
              </td>
              <td class="px-3 py-2 text-n-slate-11">
                {{ formatTime(schedule.next_send_at) }}
              </td>
              <td class="px-3 py-2">
                {{
                  schedule.enabled
                    ? $t('REPORT_SCHEDULES.STATUS.ACTIVE')
                    : $t('REPORT_SCHEDULES.STATUS.DISABLED')
                }}
              </td>
              <td class="px-3 py-2">
                <div class="flex flex-wrap justify-end gap-1">
                  <button
                    type="button"
                    class="rounded px-2 py-1 text-n-brand hover:underline"
                    @click="openEdit(schedule)"
                  >
                    {{ $t('REPORT_SCHEDULES.ACTIONS.EDIT') }}
                  </button>
                  <button
                    type="button"
                    class="rounded px-2 py-1 text-n-slate-11 hover:underline"
                    @click="toggleEnabled(schedule)"
                  >
                    {{
                      schedule.enabled
                        ? $t('REPORT_SCHEDULES.ACTIONS.DISABLE')
                        : $t('REPORT_SCHEDULES.ACTIONS.ENABLE')
                    }}
                  </button>
                  <button
                    type="button"
                    class="rounded px-2 py-1 text-n-slate-11 hover:underline"
                    @click="sendTest(schedule)"
                  >
                    {{ $t('REPORT_SCHEDULES.ACTIONS.TEST') }}
                  </button>
                  <button
                    type="button"
                    class="rounded px-2 py-1 text-n-ruby-11 hover:underline"
                    @click="removeSchedule(schedule)"
                  >
                    {{ $t('REPORT_SCHEDULES.ACTIONS.DELETE') }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-if="!loading && !schedules.length"
        class="px-5 py-8 text-center text-sm text-n-slate-11"
      >
        {{ $t('REPORT_SCHEDULES.EMPTY') }}
      </div>
    </section>

    <div
      v-if="loading"
      class="fixed inset-0 z-40 flex items-start justify-center pt-[12.5rem] bg-n-solid-1/40 pointer-events-none"
    >
      <Spinner :size="32" class="text-n-brand" />
    </div>
  </div>
</template>
